/**
 * qgsSubmissionStore
 *
 * Module-level store that keeps the operator's OK / REWORK / KO picks —
 * plus editorial details and the row's first-image URL — alive across
 * control-item navigation inside a single audit session. Without this,
 * ChecklistPage's local useState resets every time the operator moves to
 * the next control item, and by Complete we would have lost every prior
 * response.
 *
 * The store is intentionally NOT persisted to localStorage — one audit
 * session is short-lived, and a hard refresh mid-audit already discards
 * cumulativeAuditData too. Call ``beginAudit()`` when a fresh audit form
 * response is created.
 *
 * Shape (keyed by String check_id):
 *   {
 *     response:        'OK' | 'REWORK' | 'KO',
 *     details:         { defect_type_id, defect_location_id, rework_type_id, note },
 *     comment:         string,     // from the checklist row's comment cell
 *     photoUrl:        string,     // Cloudinary URL of item.images[0]
 *     categoryId:      number|string,
 *     controlItemId:   number|string,
 *     checkLabel:      string,     // convenience for logging
 *     synced:          boolean,    // true once the KO / rework has hit the backend
 *   }
 *
 * The store also tracks the backend's latest submission summary
 * (``submissionSummary`` — knockouts_count / reworks_count / status) so
 * header pills can render backend truth instead of local heuristics.
 */

const STATE = {
  submissionId: null,        // set after POST /qgs/submissions/
  submissionSummary: null,   // { knockouts_count, reworks_count, status, ... }
  records: {},
};

const listeners = new Set();


function notify() {
  listeners.forEach(fn => { try { fn(STATE); } catch (e) { /* ignore */ } });
}


export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}


export function beginAudit() {
  STATE.submissionId = null;
  STATE.submissionSummary = null;
  STATE.records = {};
  notify();
}


export function setSubmissionId(id) {
  STATE.submissionId = id;
  notify();
}


export function getSubmissionId() {
  return STATE.submissionId;
}


export function setSubmissionSummary(summary) {
  STATE.submissionSummary = summary || null;
  notify();
}


export function getSubmissionSummary() {
  return STATE.submissionSummary;
}


export function setRecord(checkId, patch) {
  if (!checkId) return;
  const key = String(checkId);
  STATE.records[key] = { ...(STATE.records[key] || {}), ...patch };
  notify();
}


export function markSynced(checkId) {
  setRecord(checkId, { synced: true });
}


export function clearRecord(checkId) {
  if (!checkId) return;
  delete STATE.records[String(checkId)];
  notify();
}


export function getRecord(checkId) {
  return STATE.records[String(checkId)] || null;
}


export function allRecords() {
  return { ...STATE.records };
}


/** Flat unsynced KO rows — ready to POST /qgs/knockouts/. */
export function pendingKoRows() {
  return Object.entries(STATE.records)
    .filter(([, r]) => r.response === 'KO' && !r.synced)
    .map(([checkId, r]) => ({ checkId, ...r }));
}


/** Flat unsynced REWORK rows — ready to POST /qgs/rework-records/. */
export function pendingReworkRows() {
  return Object.entries(STATE.records)
    .filter(([, r]) => r.response === 'REWORK' && !r.synced)
    .map(([checkId, r]) => ({ checkId, ...r }));
}


/** All KO rows, synced or not — used by the header count fallback. */
export function localKoCount() {
  return Object.values(STATE.records).filter(r => r.response === 'KO').length;
}


export function localReworkCount() {
  return Object.values(STATE.records).filter(r => r.response === 'REWORK').length;
}
